import {
  useErrorHandlingMutation,
  useErrorHandlingQuery,
} from '@backoffice-frontend/data-fetching'
import {
  FormTextarea,
  FormTextfield,
  useForm,
} from '@backoffice-frontend/forms'
import { useMoiaSuccessNotification } from '@backoffice-frontend/patterns'
import { usePaceValidators } from '@backoffice-frontend/validators'
import { Button, Dialog, LoadingIndicator } from '@moia-dev/pace-web'
import {
  useRouteBuilderSavedRouteDetailsQuery,
  useRouteBuilderUpdateSavedTripMutation,
} from '../TripEditor/TripEditor.hook'
import {
  buildUpdateSavedTripVariables,
  detailsToSegmentInputs,
  SAVED_ROUTE_DETAILS_FETCH_POLICY,
} from '../utils'
import type { SavedTrip } from './SavedTripCard'

type RenameFormValues = {
  name: string
  description: string
}

type RenameDialogProps = {
  id: string
  trip: SavedTrip
  onClose: () => void
  onSuccess: () => void
}

export const RenameDialog = ({
  id,
  trip,
  onClose,
  onSuccess,
}: RenameDialogProps) => {
  const { enqueueMoiaSuccessNotification } = useMoiaSuccessNotification()
  const { required } = usePaceValidators()

  // The update replaces the whole trip, so renaming needs the current segments
  // and allWaypointsMode — neither is on the saved-routes list entry.
  const { data: detailsData, loading: detailsLoading } = useErrorHandlingQuery(
    useRouteBuilderSavedRouteDetailsQuery,
    {
      variables: { id: trip.id },
      fetchPolicy: SAVED_ROUTE_DETAILS_FETCH_POLICY,
    },
  )
  const details = detailsData?.savedRouteDetails

  const { control, handleSubmit } = useForm<RenameFormValues>({
    defaultValues: {
      name: trip.name,
      description: trip.description ?? '',
    },
  })

  const [updateSavedTrip, { loading: saving }] = useErrorHandlingMutation(
    useRouteBuilderUpdateSavedTripMutation,
    {
      onCompleted: () => {
        enqueueMoiaSuccessNotification('Route renamed')
        onSuccess()
      },
    },
  )

  const onSubmit = ({ name, description }: RenameFormValues) => {
    updateSavedTrip({
      variables: buildUpdateSavedTripVariables({
        id: trip.id,
        name,
        description,
        tripType: trip.tripType,
        segments: detailsToSegmentInputs(details, trip.tripType),
        assignedVtrs: trip.assignedVtrs,
        allWaypointsMode: details?.trip?.allWaypointsMode ?? false,
      }),
    })
  }

  const loading = detailsLoading || saving

  return (
    <Dialog
      id={id}
      title="Rename route"
      onClose={onClose}
      actions={
        <>
          <Button
            label="Cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          />
          <Button
            label={saving ? 'Saving…' : 'Save'}
            variant="primary"
            type="submit"
            form="route-builder-rename-form"
            disabled={loading}
            icon={saving ? <LoadingIndicator size={16} /> : undefined}
          />
        </>
      }
      css={{ maxWidth: 440, minWidth: 500 }}
    >
      <form
        id="route-builder-rename-form"
        onSubmit={handleSubmit(onSubmit)}
        css={theme => ({
          padding: theme.space.Space4,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.space.Space3,
        })}
      >
        <FormTextfield
          control={control}
          name="name"
          label="Name"
          rules={{ required }}
          disabled={loading}
          autoFocus
        />
        <FormTextarea
          control={control}
          name="description"
          label="Description"
          disabled={loading}
        />
      </form>
    </Dialog>
  )
}
